import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAllReportingManagers,
  createReportingManager,
  createBulkReportingManagers,
  bulkUploadReportingManagers,
  deleteReportingManager,
  ReportingManagerRelationship
} from '@services/Feedback360Service';
import { getAllUsers, User } from '@services/AssessmentService';
import {
  getAllGroups,
  createGroup as createGroupApi,
  deleteGroup as deleteGroupApi,
  assignAssessmentToGroup,
  getGroupAssessments,
  removeAssessmentFromGroup,
  Group,
  GroupAssessment
} from '@services/GroupService';
import { getAllAssessments, Assessment } from '@services/AssessmentEligibilityService';
import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import './ReportingManagerManagement.css';
import { Layout } from '@/layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import AddRelationshipModal, { RelationshipData } from '@components/feedback360/AddRelationshipModal';
import BulkUploadRelationshipsModal, { BulkRelationshipData } from '@components/feedback360/BulkUploadRelationshipsModal';

const ReportingManagerManagement: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const location = useLocation();
  const [preSelectedAssessmentId, setPreSelectedAssessmentId] = useState<number | undefined>(undefined);

  // Read pre-selected assessment from navigation state
  useEffect(() => {
    if (location.state?.assessmentId) {
      setPreSelectedAssessmentId(location.state.assessmentId);
    }
  }, [location.state]);
  const [relationships, setRelationships] = useState<ReportingManagerRelationship[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Groups state
  const [groups, setGroups] = useState<Group[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [groupAssessments, setGroupAssessments] = useState<GroupAssessment[]>([]);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [showAssignAssessmentForm, setShowAssignAssessmentForm] = useState(false);
  const [assignAssessmentId, setAssignAssessmentId] = useState<number | undefined>(undefined);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<Group | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    userId: number;
    managerId: number | null;
    managerEmail: string;
    userName: string;
    managerName: string;
  } | null>(null);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Auto-dismiss success message after 2 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss error message after 2 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load relationships
      const relationshipsResponse = await getAllReportingManagers();
      if (relationshipsResponse.success && relationshipsResponse.data) {
        setRelationships(relationshipsResponse.data);
      } else {
        throw new Error(relationshipsResponse.error?.message || 'Failed to load relationships');
      }

      // Load all users
      const usersResponse = await getAllUsers(1, 1000);
      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data.users);
      } else {
        throw new Error(usersResponse.error?.message || 'Failed to load users');
      }

      // Load groups
      const groupsResponse = await getAllGroups();
      if (groupsResponse.success && groupsResponse.data) {
        setGroups(groupsResponse.data);
      }

      // Load assessments
      const assessmentsResponse = await getAllAssessments();
      if (assessmentsResponse.success && assessmentsResponse.data) {
        setAssessments(assessmentsResponse.data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Group management handlers
  const handleCreateGroup = async (name?: string) => {
    const groupName = name || newGroupName.trim();
    if (!groupName) return null;

    try {
      const response = await createGroupApi(groupName, newGroupDescription.trim() || undefined);
      if (response.success && response.data) {
        setSuccessMessage(`Group "${groupName}" created successfully!`);
        setShowGroupForm(false);
        setNewGroupName('');
        setNewGroupDescription('');
        // Reload groups
        const groupsResponse = await getAllGroups();
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        }
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to create group');
      }
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupTarget) return;

    try {
      const response = await deleteGroupApi(deleteGroupTarget.id);
      if (response.success) {
        setSuccessMessage(`Group "${deleteGroupTarget.name}" deleted successfully!`);
        setShowDeleteGroupModal(false);
        setDeleteGroupTarget(null);
        if (expandedGroupId === deleteGroupTarget.id) {
          setExpandedGroupId(null);
        }
        // Reload groups
        const groupsResponse = await getAllGroups();
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to delete group');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleExpandGroup = async (groupId: number) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      setGroupAssessments([]);
      setShowAssignAssessmentForm(false);
      return;
    }

    setExpandedGroupId(groupId);
    setShowAssignAssessmentForm(false);

    try {
      const response = await getGroupAssessments(groupId);
      if (response.success && response.data) {
        setGroupAssessments(response.data);
      }
    } catch (err: any) {
      console.error('Error loading group assessments:', err);
    }
  };

  const handleAssignAssessment = async () => {
    if (!expandedGroupId || !assignAssessmentId) return;

    try {
      const response = await assignAssessmentToGroup(expandedGroupId, assignAssessmentId);
      if (response.success) {
        const data = response.data;
        setSuccessMessage(
          `Assessment assigned! ${data?.newly_assigned || 0} users assigned, ${data?.already_eligible || 0} already eligible.`
        );
        setShowAssignAssessmentForm(false);
        setAssignAssessmentId(undefined);
        // Reload group assessments
        const assessmentsResponse = await getGroupAssessments(expandedGroupId);
        if (assessmentsResponse.success && assessmentsResponse.data) {
          setGroupAssessments(assessmentsResponse.data);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to assign assessment');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveAssessment = async (assessmentId: number) => {
    if (!expandedGroupId) return;

    try {
      const response = await removeAssessmentFromGroup(expandedGroupId, assessmentId);
      if (response.success) {
        setSuccessMessage('Assessment removed from group');
        // Reload group assessments
        const assessmentsResponse = await getGroupAssessments(expandedGroupId);
        if (assessmentsResponse.success && assessmentsResponse.data) {
          setGroupAssessments(assessmentsResponse.data);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to remove assessment');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddRelationship = async (relationshipData: RelationshipData) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowAddModal(false);

    try {
      // Separate managers into existing users and manual entries
      const existingManagerIds = relationshipData.managers
        .filter(m => m.manager_id !== undefined)
        .map(m => m.manager_id!);

      const manualManagers = relationshipData.managers.filter(
        m => m.manager_name && m.manager_email
      );

      let successCount = 0;
      let failedCount = 0;

      // Create relationships with existing managers (bulk)
      if (existingManagerIds.length > 0) {
        const response = await createBulkReportingManagers({
          user_id: relationshipData.user_id,
          manager_ids: existingManagerIds
        });

        if (response.success) {
          successCount += existingManagerIds.length;
        } else {
          failedCount += existingManagerIds.length;
          throw new Error(response.error?.message || 'Failed to create relationships with existing managers');
        }
      }

      // Create relationships with manual managers (one by one for now)
      for (const manualManager of manualManagers) {
        try {
          const response = await createReportingManager({
            user_id: relationshipData.user_id,
            reporting_manager_id: 0,
            manager_name: manualManager.manager_name,
            manager_email: manualManager.manager_email
          } as any);

          if (response.success) {
            successCount++;
          } else {
            failedCount++;
          }
        } catch (err) {
          failedCount++;
          console.error('Error creating manual manager relationship:', err);
        }
      }

      if (successCount > 0) {
        setSuccessMessage(`Successfully created ${successCount} reporting manager relationship${successCount > 1 ? 's' : ''}!${failedCount > 0 ? ` (${failedCount} failed)` : ''}`);
        await loadData();
      } else {
        throw new Error('Failed to create any relationships');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create relationships');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (bulkData: BulkRelationshipData[], assessmentId?: number, groupId?: number) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowBulkUploadModal(false);

    try {
      const response = await bulkUploadReportingManagers({
        relationships: bulkData,
        assessment_id: assessmentId,
        group_id: groupId
      });

      if (response.success) {
        const data = response.data?.data || response.data;
        const parts = [];
        if (data?.new_relationships) parts.push(`${data.new_relationships} relationships created`);
        if (data?.created_users) parts.push(`${data.created_users} new users onboarded`);
        if (data?.assessment_assigned) parts.push(`${data.assessment_assigned} assigned to assessment`);
        if (data?.group_assigned) parts.push(`${data.group_assigned} added to group`);

        setSuccessMessage(
          parts.length > 0
            ? `Upload successful! ${parts.join(', ')}.`
            : `Successfully uploaded ${bulkData.length} relationship${bulkData.length > 1 ? 's' : ''}!`
        );
        await loadData();
      } else {
        throw new Error(response.error?.message || 'Failed to upload relationships');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload relationships');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (
    userId: number,
    managerId: number | null,
    managerEmail: string,
    userName: string,
    managerName: string
  ) => {
    setDeleteTarget({ userId, managerId, managerEmail, userName, managerName });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDeleteRelationship = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowDeleteModal(false);

    try {
      const response = await deleteReportingManager(
        deleteTarget.userId,
        deleteTarget.managerId,
        deleteTarget.managerEmail
      );

      if (response.success) {
        setSuccessMessage('Relationship deleted successfully!');
        await loadData();
      } else {
        throw new Error(response.error?.message || 'Failed to delete relationship');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  // Filter relationships
  const filteredRelationships = relationships.filter(rel => {
    const matchesSearch = searchTerm === '' ||
      rel.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.manager_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.manager_email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = !filterUser || rel.user_id === filterUser.userId;

    return matchesSearch && matchesFilter;
  });

  const readyAssessments = assessments.filter(a => a.status === 'ready');

  return (
    <Layout>
    <div className="reporting-manager-management" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Global Loading Overlay - Only show when performing operations (not initial load) */}
      {loading && relationships.length > 0 && (
        <div className="reporting-manager-loading-overlay">
          <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
        </div>
      )}

      <div className="reporting-manager-page-header">
        <div className="reporting-manager-header-content">
          <h1>Reporting Manager Management</h1>
          <p className="reporting-manager-subtitle">Manage reporting manager relationships for 180° feedback assessments</p>
        </div>
        <div className="reporting-manager-header-actions">
          <button
            className="reporting-manager-btn-secondary"
            onClick={() => setShowBulkUploadModal(true)}
            disabled={loading}
            title="Upload multiple relationships via CSV"
          >
            Bulk Upload
          </button>
          <button
            className="reporting-manager-btn-primary"
            onClick={() => setShowAddModal(true)}
            disabled={loading}
          >
            + Add Relationship
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="reporting-manager-alert reporting-manager-alert-error">
          <span className="reporting-manager-alert-icon">!</span>
          {error}
          <button className="reporting-manager-alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="reporting-manager-alert reporting-manager-alert-success">
          <span className="reporting-manager-alert-icon">OK</span>
          {successMessage}
          <button className="reporting-manager-alert-close" onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Groups Section */}
      <div className="reporting-manager-groups-section">
        <div className="reporting-manager-groups-header">
          <h2>Groups</h2>
          <button
            className="reporting-manager-btn-primary reporting-manager-btn-sm"
            onClick={() => setShowGroupForm(!showGroupForm)}
            disabled={loading}
          >
            {showGroupForm ? 'Cancel' : '+ New Group'}
          </button>
        </div>

        {/* Create Group Form */}
        {showGroupForm && (
          <div className="reporting-manager-group-form">
            <input
              type="text"
              className="reporting-manager-form-control"
              placeholder="Group name (e.g., company name)..."
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateGroup();
              }}
            />
            <input
              type="text"
              className="reporting-manager-form-control"
              placeholder="Description (optional)..."
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
            />
            <button
              className="reporting-manager-btn-primary reporting-manager-btn-sm"
              onClick={() => handleCreateGroup()}
              disabled={!newGroupName.trim()}
            >
              Create Group
            </button>
          </div>
        )}

        {/* Groups List */}
        {groups.length === 0 ? (
          <p className="reporting-manager-groups-empty">No groups yet. Create one to organize users by company or team.</p>
        ) : (
          <div className="reporting-manager-groups-list">
            {groups.map((group) => (
              <div key={group.id} className={`reporting-manager-group-card ${expandedGroupId === group.id ? 'expanded' : ''}`}>
                <div
                  className="reporting-manager-group-card-header"
                  onClick={() => handleExpandGroup(group.id)}
                >
                  <div className="reporting-manager-group-info">
                    <span className="reporting-manager-group-name">{group.name}</span>
                    {group.description && (
                      <span className="reporting-manager-group-desc">{group.description}</span>
                    )}
                  </div>
                  <div className="reporting-manager-group-stats">
                    <Chip label={`${group.member_count} members`} size="small" variant="outlined" />
                    <Chip label={`${group.assessment_count} assessments`} size="small" variant="outlined" />
                    <button
                      className="reporting-manager-btn-delete-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteGroupTarget(group);
                        setShowDeleteGroupModal(true);
                      }}
                      title="Delete group"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Expanded Group Details */}
                {expandedGroupId === group.id && (
                  <div className="reporting-manager-group-details">
                    <div className="reporting-manager-group-assessments">
                      <div className="reporting-manager-group-assessments-header">
                        <h4>Assigned Assessments</h4>
                        <button
                          className="reporting-manager-btn-primary reporting-manager-btn-sm"
                          onClick={() => setShowAssignAssessmentForm(!showAssignAssessmentForm)}
                        >
                          {showAssignAssessmentForm ? 'Cancel' : '+ Assign Assessment'}
                        </button>
                      </div>

                      {showAssignAssessmentForm && (
                        <div className="reporting-manager-assign-form">
                          <select
                            className="reporting-manager-form-control"
                            value={assignAssessmentId || ''}
                            onChange={(e) => setAssignAssessmentId(e.target.value ? parseInt(e.target.value) : undefined)}
                          >
                            <option value="">-- Select Assessment --</option>
                            {readyAssessments.map((a) => (
                              <option key={a.assessmentId} value={a.assessmentId}>
                                {a.assessmentTitle}
                              </option>
                            ))}
                          </select>
                          <button
                            className="reporting-manager-btn-primary reporting-manager-btn-sm"
                            onClick={handleAssignAssessment}
                            disabled={!assignAssessmentId}
                          >
                            Assign
                          </button>
                        </div>
                      )}

                      {groupAssessments.length === 0 ? (
                        <p className="reporting-manager-groups-empty">No assessments assigned to this group yet.</p>
                      ) : (
                        <div className="reporting-manager-group-assessment-list">
                          {groupAssessments.map((ga) => (
                            <div key={ga.assignment_id} className="reporting-manager-group-assessment-item">
                              <span className="reporting-manager-group-assessment-title">{ga.assessmentTitle}</span>
                              <Chip
                                label={ga.status}
                                size="small"
                                color={ga.status === 'ready' ? 'success' : 'default'}
                                variant="outlined"
                              />
                              <button
                                className="reporting-manager-btn-delete-small"
                                onClick={() => handleRemoveAssessment(ga.assessment_id)}
                                title="Remove from group"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search and Filter */}
      <div className="reporting-manager-filters-container">
        <div className="reporting-manager-search-box">
          <input
            type="text"
            className="reporting-manager-form-control"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="reporting-manager-filter-box">
          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.name}
            value={filterUser}
            onChange={(event, newValue) => {
              setFilterUser(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filter by user..."
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '4px',
              }
            }}
          />
        </div>
      </div>

      {/* Relationships Table */}
      <div className="reporting-manager-relationships-container">
        {loading && relationships.length === 0 ? (
          <div className="reporting-manager-loading-state">
            <CircularProgress size={48} thickness={4} sx={{ color: '#667eea', marginBottom: '20px' }} />
            <p>Loading relationships...</p>
          </div>
        ) : filteredRelationships.length === 0 ? (
          <div className="reporting-manager-empty-state">
            <div className="reporting-manager-empty-icon">list</div>
            <h3>No Reporting Relationships Found</h3>
            <p>{searchTerm || filterUser ? 'No relationships match your search or filter.' : 'Click "Add Relationship" to create reporting manager assignments.'}</p>
          </div>
        ) : (
          <div className="reporting-manager-table-wrapper">
            <table className="reporting-manager-relationships-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Employee Email</th>
                  <th>Manager Name</th>
                  <th>Manager Email</th>
                  <th className="reporting-manager-text-center">Created Date</th>
                  <th className="reporting-manager-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRelationships.map((rel) => (
                  <tr key={rel.id}>
                    <td>
                      <div className="reporting-manager-user-cell">
                        <span className="reporting-manager-user-name">{rel.user_name}</span>
                      </div>
                    </td>
                    <td className="reporting-manager-email-cell">{rel.user_email}</td>
                    <td>
                      <div className="reporting-manager-manager-cell">
                        <span className="reporting-manager-manager-name">{rel.manager_name}</span>
                      </div>
                    </td>
                    <td className="reporting-manager-email-cell">{rel.manager_email}</td>
                    <td className="reporting-manager-text-center reporting-manager-date-cell">
                      {new Date(rel.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="reporting-manager-text-center">
                      <button
                        className="reporting-manager-btn-delete-table"
                        onClick={() => openDeleteModal(
                          rel.user_id,
                          rel.reporting_manager_id,
                          rel.manager_email,
                          rel.user_name,
                          rel.manager_name
                        )}
                        disabled={loading}
                        title="Remove relationship"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Relationship Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="reporting-manager-modal-overlay" onClick={closeDeleteModal}>
          <div className="reporting-manager-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="reporting-manager-modal-header">
              <h3>Confirm Deletion</h3>
              <button className="reporting-manager-modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="reporting-manager-modal-body">
              <p>Are you sure you want to remove this reporting manager relationship?</p>
              <div className="reporting-manager-delete-details">
                <div className="reporting-manager-detail-row">
                  <strong>Employee:</strong> {deleteTarget.userName}
                </div>
                <div className="reporting-manager-detail-row">
                  <strong>Manager:</strong> {deleteTarget.managerName}
                </div>
              </div>
              <p className="reporting-manager-warning-text">This action cannot be undone.</p>
            </div>
            <div className="reporting-manager-modal-footer">
              <button className="reporting-manager-btn-secondary" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button className="reporting-manager-btn-danger" onClick={handleDeleteRelationship}>
                Delete Relationship
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupModal && deleteGroupTarget && (
        <div className="reporting-manager-modal-overlay" onClick={() => setShowDeleteGroupModal(false)}>
          <div className="reporting-manager-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="reporting-manager-modal-header">
              <h3>Delete Group</h3>
              <button className="reporting-manager-modal-close" onClick={() => setShowDeleteGroupModal(false)}>×</button>
            </div>
            <div className="reporting-manager-modal-body">
              <p>Are you sure you want to delete the group <strong>"{deleteGroupTarget.name}"</strong>?</p>
              <p>This will remove the group association from {deleteGroupTarget.member_count} member(s). Users will not be deleted.</p>
              <p className="reporting-manager-warning-text">This action cannot be undone.</p>
            </div>
            <div className="reporting-manager-modal-footer">
              <button className="reporting-manager-btn-secondary" onClick={() => setShowDeleteGroupModal(false)}>
                Cancel
              </button>
              <button className="reporting-manager-btn-danger" onClick={handleDeleteGroup}>
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Relationship Modal */}
      <AddRelationshipModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddRelationship}
        users={users}
        loading={loading}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadRelationshipsModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSubmit={handleBulkUpload}
        assessments={assessments}
        groups={groups}
        onCreateGroup={async (name: string) => {
          const group = await handleCreateGroup(name);
          return group;
        }}
        defaultAssessmentId={preSelectedAssessmentId}
      />
    </div>
    </Layout>
  );
};

export default ReportingManagerManagement;
